<script setup lang="ts">
  import type { PropType } from 'vue'

  import { computed, ref, watch } from 'vue'

  import CPButton from '@ui/components/CPButton.vue'

  import CPModal from '@ui/components/CPModal.vue'

  import CPText from '@ui/components/CPText.vue'

  import { createElementId } from '../../core-layer/utils'

  import type { ActionButton, ModalContent } from '../types/confirmationmodal'

  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    content: {
      type: Object as PropType<ModalContent>,
      default: () => ({}),
    },
    actions: {
      type: Array as PropType<ActionButton[]>,
      default: () => [],
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    width: {
      type: Number,
      default: 500,
    },
    zIndex: {
      type: Number,
      default: undefined,
    },
    /** First segment of footer action button ids (createElementId). Use a feature-specific value when multiple confirmation modals may exist. */
    actionButtonIdPage: {
      type: String,
      default: 'modal',
    },
  })

  const emit = defineEmits<{
    'update:show': [value: boolean]
    action: [actionKey: string]
    close: []
  }>()

  const showModal = ref(false)

  const handleAction = (actionKey: string) => {
    emit('action', actionKey)
  }

  const handleClose = () => {
    showModal.value = false
  }

  // Icon styling classes
  const iconClasses = computed(() => {
    const baseClasses = 'my-2 h-12 w-12 rounded-full p-1'
    const typeClasses = {
      success: 'bg-success-100 border-8 border-solid border-success-50 text-success-400',
      error: 'bg-error-100 border-8 border-solid border-error-50 text-error-400',
      warning: 'bg-warning-100 border-8 border-solid border-warning-50 text-warning-400',
      primary: 'bg-primary-100 border-8 border-solid border-primary-50 text-primary-400',
      custom: 'bg-surface border-8 border-solid border-[var(--cp-border-100)] text-secondary',
    }
    return `${baseClasses} ${typeClasses[props.content.iconType || 'primary']}`
  })

  // Watch for show prop changes
  watch(
    () => props.show,
    newValue => {
      showModal.value = newValue
    },
    { immediate: true }
  )

  // Watch for internal modal state changes
  watch(
    () => showModal.value,
    newValue => {
      emit('update:show', newValue)
      if (!newValue) {
        emit('close')
      }
    }
  )
</script>

<template>
  <CPModal
    :id="createElementId('confirmation', 'modal', '')"
    :show="showModal"
    :width="props.width"
    :z-index="props.zIndex"
    :show-close="props.showCloseButton"
    :show-header-icon="false"
    :show-footer="true"
    class="px-4 py-3 confirmation-modal"
    @update:show="handleClose"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <!-- Icon -->
        <div v-if="props.content.icon" :class="iconClasses">
          <component :is="props.content.icon" />
        </div>

        <!-- Title -->
        <CPText v-if="props.content.title" size="lg" weight="semibold" class="text-body">
          {{ props.content.title }}
        </CPText>
      </div>
    </template>

    <template #default>
      <!-- Message -->
      <div v-if="props.content.message" class="text-700">
        <CPText v-if="!props.content.isMessageHtml" size="md" class="leading-relaxed">
          {{ props.content.message }}
        </CPText>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-else v-sanitized-html="props.content.message" class="prose prose-sm max-w-none" />
      </div>

      <!-- Default Slot for Custom Content -->
      <slot />
    </template>

    <template #footer>
      <div v-if="props.actions.length > 0" class="flex items-center justify-end gap-3">
        <CPButton
          v-for="action in props.actions"
          :id="createElementId(props.actionButtonIdPage, 'footer', `${action.key}-button`)"
          :key="action.key"
          :type="action.type || 'secondary'"
          :color="action.color || 'primary'"
          :disabled="action.disabled"
          :loading="action.loading"
          @click="handleAction(action.key)"
        >
          {{ action.label }}
        </CPButton>
      </div>
    </template>
  </CPModal>
</template>

<style scoped>
  /* Prose styles for HTML content */
  :deep(.prose) {
    color: inherit;
  }

  :deep(.prose p) {
    margin: 0.5em 0;
  }

  :deep(.prose ul) {
    padding-left: 1.5em;
    margin: 0.5em 0;
  }

  :deep(.prose li) {
    margin: 0.25em 0;
  }
</style>
<style>
  .confirmation-modal.hr-dialog.hr-modal .hr-dialog__title {
    padding: 4px !important;
  }
  .confirmation-modal.hr-dialog.hr-modal .hr-text.hr-text-md.hr-text-regular.hr-modal__content {
    padding: 16px 0px !important;
  }
  .confirmation-modal .hr-dialog__content.hr-dialog__content--last {
    padding: 0px 4px !important;
    margin: 0px !important;
  }
</style>
